/**
 * Nova tarefa do setor: formulário curto (título, quem, onde, ambiente ou
 * peça, prazos e descrição). Aberta do cabeçalho, do "+" de uma coluna do
 * kanban (já com o subsetor), do painel do ambiente (já ligada a ele) ou do
 * painel da peça desmembrada (já ligada à peça e ao ambiente de origem dela).
 */
import React, { useEffect, useState } from "react";
import { FiScissors } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { CampoAmbiente, CampoColaborador, CamposLugar, limparEspacos } from "./formularios";
import { Acoes, Ajuda, Campo, Campos, Secao } from "./styles";
import { ErroDaAcao, useErroDeAcao } from "./ui";

const TAMANHO_TITULO = 200;
const TAMANHO_DESCRICAO = 4000;

function formularioInicial(inicial) {
  return {
    titulo: "",
    colaboradorId: inicial?.colaboradorId || null,
    subsetorId: inicial?.subsetorId || null,
    postoId: inicial?.postoId || null,
    ambiente: inicial?.ambiente || null,
    peca: inicial?.peca || null,
    prevista: "",
    limite: "",
    descricao: "",
  };
}

/** Corpo do POST /tarefas a partir do formulário. */
export function dadosDaNovaTarefa(setorId, form) {
  return {
    setor_id: setorId,
    titulo: limparEspacos(form.titulo),
    descricao: form.descricao.trim(),
    colaborador_id: form.colaboradorId || null,
    subsetor_id: form.subsetorId || null,
    posto_id: form.postoId || null,
    // A tarefa de uma peça guarda também o ambiente de origem dela: é por ele
    // que a tarefa aparece na linha do tempo do ambiente.
    ambiente_chave: form.peca?.ambienteChave || form.ambiente?.chave || "",
    ambiente_rotulo: form.peca?.ambienteRotulo || form.ambiente?.rotulo || "",
    peca_id: form.peca?.id || null,
    data_prevista: form.prevista || null,
    data_limite: form.limite || null,
  };
}

/**
 * Props: aberto, inicial ({subsetorId, postoId, colaboradorId, ambiente,
 * peca}), setorId, setorNome, estado, demandas (do setor), acoes, onFechar,
 * onCriada(tarefa), onRecarregar.
 */
export default function NovaTarefa({
  aberto,
  inicial,
  setorId,
  setorNome = "",
  estado,
  demandas,
  acoes,
  onFechar,
  onCriada,
  onRecarregar,
}) {
  const [form, setForm] = useState(() => formularioInicial(inicial));
  const { erro, rodar, limpar } = useErroDeAcao();
  useEffect(() => {
    if (aberto) {
      setForm(formularioInicial(inicial));
      limpar();
    }
  }, [aberto, inicial, limpar]);

  const semTitulo = !limparEspacos(form.titulo);
  const mudar = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));

  const criar = () =>
    rodar(async () => {
      const tarefa = await acoes.criarTarefa(dadosDaNovaTarefa(setorId, form));
      onCriada(tarefa);
      return tarefa;
    });

  return (
    <SlideOverPanel
      open={aberto}
      onClose={onFechar}
      title="Nova tarefa"
      subtitle={setorNome}
      width="min(96vw, 560px)"
      footer={
        <Acoes data-fim="sim">
          <button type="button" className="btn btn--secondary" onClick={onFechar}>
            Cancelar
          </button>
          <AsyncButton silentError disabled={semTitulo} successMessage="Tarefa criada." onClick={criar}>
            Criar tarefa
          </AsyncButton>
        </Acoes>
      }
    >
      <ErroDaAcao erro={erro} onRecarregar={onRecarregar} />
      <Secao>
        <Campo>
          Título
          <input
            value={form.titulo}
            maxLength={TAMANHO_TITULO}
            onChange={mudar("titulo")}
            onBlur={() => setForm((f) => ({ ...f, titulo: limparEspacos(f.titulo) }))}
            placeholder="Ex.: Montar gaveteiro da cozinha"
          />
        </Campo>
        <CampoColaborador
          setorId={setorId}
          colaboradores={estado?.colaboradores}
          valor={form.colaboradorId}
          onChange={(colaboradorId) => setForm((f) => ({ ...f, colaboradorId }))}
          rotulo="Atribuir a"
        />
        <CamposLugar
          setorId={setorId}
          estado={estado}
          subsetorId={form.subsetorId}
          postoId={form.postoId}
          onChange={(lugar) => setForm((f) => ({ ...f, ...lugar }))}
        />
        {form.peca ? (
          <div>
            <Campo as="div">
              Peça desmembrada
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5 }}>
                <FiScissors size={12} aria-hidden="true" /> {form.peca.rotulo}
              </span>
            </Campo>
            <Ajuda>
              A tarefa fica ligada a esta peça e ao ambiente de origem dela
              ({form.peca.ambienteRotulo || "não carregado"}).
            </Ajuda>
          </div>
        ) : (
          <CampoAmbiente
            demandas={demandas}
            valor={form.ambiente}
            onChange={(ambiente) => setForm((f) => ({ ...f, ambiente }))}
          />
        )}
        <Campos $min={160}>
          <Campo>
            Previsão
            <input type="date" value={form.prevista} onChange={mudar("prevista")} />
          </Campo>
          <Campo>
            Data limite
            <input type="date" value={form.limite} onChange={mudar("limite")} />
          </Campo>
        </Campos>
        <Campo>
          Descrição
          <textarea value={form.descricao} maxLength={TAMANHO_DESCRICAO} onChange={mudar("descricao")} />
        </Campo>
      </Secao>
    </SlideOverPanel>
  );
}
